import Dialog from '@anm/components/modals/Dialog';
import ConfirmDelete from '@anm/components/notification/ConfirmDelete';
import { WorkspaceContext } from '@anm/contexts/WorkspaceContext';
import useEventPending from '@anm/hooks/useEventPending';
import { appNotificationActions } from '@anm/store/modules/appNotifications';
import noop from 'lodash/fp/noop';
import { useContext, useState, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import withVideoFolder, { WithVideoFolder } from '../../../../HOCs/withVideoFolder';
import { videoFolderSelectors } from '../../../../store/modules/videoFolder';
import { ApplicationState } from '../../../../store/types';

import useDeleteNestedFoldersAndProjects from './useDeleteNestedFoldersAndProjects';

type HOCs = WithVideoFolder;
export type DeleteFolderModalProps = {
  folderId: number;
  isPending?: boolean;
  description?: string;
  onDelete: () => void;
  onClose?: () => void;
};
type DeleteFolderHandler = {
  onDeleteFolder(): void;
};

const ConfirmFolderDelete: FC<HOCs & DeleteFolderModalProps & DeleteFolderHandler> = ({
  isPending,
  videoFolder,
  description,
  onDeleteFolder,
  onClose,
  onDelete
}) => {
  const statEventPending = useEventPending(videoFolder.deleteFolder.isPending, onDelete);

  return (
    <ConfirmDelete
      description={description}
      isPending={isPending || videoFolder.deleteFolder.isPending}
      deleteItemName="folder"
      onClose={onClose}
      onDelete={() => {
        onDeleteFolder();
        statEventPending();
      }}
    />
  );
};

const Notification: FC<HOCs & DeleteFolderModalProps & { onDeleteFolder(): void }> = props => {
  const { folderId } = props;
  const dispatch = useDispatch();
  const isFolderEmpty = useSelector(videoFolderSelectors.isFolderEmpty);
  const isFolderExist = useSelector(videoFolderSelectors.isFolderExist);
  const folder = useSelector((state: ApplicationState) => videoFolderSelectors.findFolderInTree(state)(folderId));

  const { workspace } = useContext(WorkspaceContext);
  const [isPending, setIsPending] = useState(false);

  const [
    totalProjects,
    foldersCount,
    objectDeleted,
    handleDeleteNestedFoldersAndProjects
  ] = useDeleteNestedFoldersAndProjects(workspace, folder);

  if (!isFolderExist(folderId)) return null;

  const handleDelete = async () => {
    if (isFolderEmpty(folderId)) props.onDeleteFolder();
    else {
      setIsPending(true);
      try {
        await handleDeleteNestedFoldersAndProjects();
        props.onDeleteFolder();
      } catch (error) {
        dispatch(appNotificationActions.notify({ type: 'error', description: error.message }));
      } finally {
        setIsPending(false);
      }
    }
  };

  const progress = Math.floor((objectDeleted / (totalProjects + foldersCount)) * 100);

  const description = isFolderEmpty(folderId)
    ? `This folder will be deleted forever.  <br/>Are you sure?`
    : `<b>${totalProjects} projects and ${foldersCount} folders</b> will be deleted forever.
     <br/>${isPending || progress === 100 ? `${progress}% Deleting ...` : 'Are you sure?'}`;

  return (
    <ConfirmFolderDelete {...props} isPending={isPending} description={description} onDeleteFolder={handleDelete} />
  );
};

const DeleteFolderModal: FC<HOCs & DeleteFolderModalProps> = props => {
  const deleteFolder = () => props.deleteFolder({ folderId: props.folderId });

  return (
    <Dialog onModalClose={props.onClose || noop} onSubmit={deleteFolder}>
      <Notification {...props} onDeleteFolder={deleteFolder} />
    </Dialog>
  );
};

export default withVideoFolder(DeleteFolderModal);
